import { useState } from "react";
import { apiFetch } from "../../../context/AuthContext";

const useArchives = ({ BASE_URL, profile, selectedCompanyName, setConfig, setActiveTab }) => {
  const [archivedConfigs, setArchivedConfigs] = useState([]);
  const [archivesLoading, setArchivesLoading] = useState(false);
  const [selectedArchiveId, setSelectedArchiveId] = useState(null);
  const [isEditingArchive, setIsEditingArchive] = useState(false);

  const loadArchives = async (companyName = null) => {
    if (!companyName) companyName = selectedCompanyName;
    if (!companyName) return;
    try {
      setArchivesLoading(true);
      const clientName = profile?.clientName || localStorage.getItem("clientName");
      const url = `${BASE_URL}/api/company-config/${clientName}/${encodeURIComponent(
        companyName
      )}/archive/history?limit=100`;
      const resp = await apiFetch(url, { method: "GET" });
      if (resp.ok) {
        const d = await resp.json();
        if (d.success && d.data) {
          setArchivedConfigs(d.data || []);
        }
      }
    } catch (err) {
      console.error("Failed to load archives:", err);
    } finally {
      setArchivesLoading(false);
    }
  };

  const selectArchive = (archive) => {
    if (!archive) return;
    setSelectedArchiveId(archive.id);
    setIsEditingArchive(true);
    // Filter out DB-only metadata fields so they don't leak into config state
    const { id, createdAt, created_at, updatedAt, updated_at, archiveReason, ...archiveConfig } = archive;

    const parsedAttendanceRules = (() => {
      if (!archive.attendanceRules) return {};
      return typeof archive.attendanceRules === "string"
        ? JSON.parse(archive.attendanceRules)
        : archive.attendanceRules;
    })();

    setConfig((prev) => ({
      ...prev,
      ...archiveConfig,
      department: "",
      departmentRules: parsedAttendanceRules,
      attendanceRules: parsedAttendanceRules,
      // Always assigned, never inherited from whatever was in the form before.
      // Archives written before location snapshotting have NULL here, which the
      // editor must show as "no areas" rather than as the live config's areas.
      defaultLocations: Array.isArray(archive.defaultLocations)
        ? archive.defaultLocations
        : [],
      allowCompanyDefaultLocation: archive.allowCompanyDefaultLocation ?? true,
    }));
    setActiveTab("attendance");
  };

  const updateArchive = async (archiveId, config, showAlert, loadConfiguration, prepareAdvancementConfigForSave) => {
    if (!archiveId) {
      showAlert("Warning", "No archive selected", "warning");
      return;
    }
    try {
      const clientName = profile?.clientName || localStorage.getItem("clientName");
      const companyName = selectedCompanyName;

      const {
        companyName: _cn,
        industry,
        email,
        phone,
        address,
        website,
        department,
        departmentRules,
        ...editableConfig
      } = config;

      // Base attendanceRules map starting from existing attendanceRules
      let baseAttendanceRules = {};
      if (editableConfig.attendanceRules) {
        baseAttendanceRules =
          typeof editableConfig.attendanceRules === "string"
            ? JSON.parse(editableConfig.attendanceRules)
            : { ...editableConfig.attendanceRules };
      }

      const mergedAttendanceRules = { ...baseAttendanceRules };

      // Helper to build a complete department rules payload merging department state with company defaults
      const buildDeptRulesPayload = (deptData = {}) => ({
        workStartTime: deptData.workStartTime ?? editableConfig.workStartTime,
        workEndTime: deptData.workEndTime ?? editableConfig.workEndTime,
        isOvernightShift: Boolean(deptData.isOvernightShift ?? editableConfig.isOvernightShift),
        shiftDayPivotHour: (() => {
          const val = deptData.shiftDayPivotHour ?? editableConfig.shiftDayPivotHour;
          return val === "" || val === null || val === undefined ? null : Number(val);
        })(),
        workingHoursPerDay: deptData.workingHoursPerDay ?? editableConfig.workingHoursPerDay,
        workingDaysPerWeek: deptData.workingDaysPerWeek ?? editableConfig.workingDaysPerWeek,
        weekendDays: deptData.weekendDays ?? editableConfig.weekendDays,
        lateDeductionRules: deptData.lateDeductionRules ?? editableConfig.lateDeductionRules,
        absenceDeductionPerDay: deptData.absenceDeductionPerDay ?? editableConfig.absenceDeductionPerDay,
        overtimeEnabled: deptData.overtimeEnabled ?? editableConfig.overtimeEnabled,
        overtimeRate: deptData.overtimeRate ?? editableConfig.overtimeRate,
        overtimeStartAfter: deptData.overtimeStartAfter ?? editableConfig.overtimeStartAfter,
        maxOvertimeHoursPerDay: deptData.maxOvertimeHoursPerDay ?? editableConfig.maxOvertimeHoursPerDay,
        halfDayRequestThreshold: deptData.halfDayRequestThreshold ?? editableConfig.halfDayRequestThreshold,
        allowDefaultLocation: deptData.allowDefaultLocation ?? editableConfig.allowDefaultLocation ?? true,
      });

      // Merge all entries from config.departmentRules into mergedAttendanceRules
      if (departmentRules && typeof departmentRules === "object") {
        Object.keys(departmentRules).forEach((deptKey) => {
          const deptData = departmentRules[deptKey];
          if (deptData && typeof deptData === "object") {
            mergedAttendanceRules[deptKey] = {
              ...(mergedAttendanceRules[deptKey] || {}),
              ...buildDeptRulesPayload(deptData),
            };
          }
        });
      }

      // If a specific department is currently active, ensure its department rules are explicitly updated
      if (department) {
        const activeDeptData = (departmentRules && departmentRules[department]) || {};
        mergedAttendanceRules[department] = {
          ...(mergedAttendanceRules[department] || {}),
          ...buildDeptRulesPayload(activeDeptData),
        };
      }

      const advancementConfig = prepareAdvancementConfigForSave
        ? prepareAdvancementConfigForSave(editableConfig)
        : {};

      const {
        advancementEnabled: _ae,
        maxAdvancementAmount: _maa,
        maxAdvancementPercentOfSalary: _maps,
        minMonthsOfService: _mmos,
        maxActiveAdvancements: _maa2,
        maxAdvancementsPerMonth: _mapm,
        maxMonthlyAdvancementTotal: _mmat,
        defaultInstallments: _di,
        minInstallments: _mini,
        maxInstallments: _maxi,
        requireManagerApproval: _rma,
        requireHRApproval: _rha,
        autoApproveUnderAmount: _aaua,
        advancementProcessingDays: _apd,
        allowEmergencyAdvancements: _aea,
        emergencyMaxAmount: _ema,
        deductionStartDelay: _dsd,
        blockAdvancementIfPending: _baip,
        blockAdvancementIfOverdue: _baio,
        advancementNotifications: _an,
        ...otherConfig
      } = editableConfig;

      const payload = {
        clientName,
        companyName,
        isOvernightShift: editableConfig.isOvernightShift,
        shiftDayPivotHour:
          editableConfig.shiftDayPivotHour === "" ||
          editableConfig.shiftDayPivotHour === null ||
          editableConfig.shiftDayPivotHour === undefined
            ? null
            : Number(editableConfig.shiftDayPivotHour),
        ...otherConfig,
        ...advancementConfig,
        attendanceRules: mergedAttendanceRules,
      };

      // Working-company locations have no per-department override, so a save
      // made while a department is selected must not write them onto the
      // archive. Mirrors COMPANY_WIDE_ATTENDANCE_FIELDS on the live-config save.
      if (department) {
        delete payload.defaultLocations;
        delete payload.allowCompanyDefaultLocation;
      }

      const url = `${BASE_URL}/api/company-config/${clientName}/${encodeURIComponent(
        companyName
      )}/archive/${archiveId}`;

      const resp = await apiFetch(url, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const data = await resp.json();
      if (!resp.ok || !data.success) throw new Error(data.message || "Failed to update archive");

      showAlert("Success", "Archive updated successfully", "success");
      await loadArchives(companyName);
      setIsEditingArchive(false);
      setSelectedArchiveId(null);
      await loadConfiguration(companyName);
    } catch (err) {
      console.error("Error updating archive:", err);
      showAlert("Error", err.message || "Failed to update archive", "error");
    }
  };

  return {
    archivedConfigs,
    archivesLoading,
    selectedArchiveId,
    setSelectedArchiveId,
    isEditingArchive,
    setIsEditingArchive,
    loadArchives,
    selectArchive,
    updateArchive,
  };
};

export default useArchives;